import { useQueryClient, useMutation } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  SendWorkbenchTransactionsInput,
  SendWorkbenchTransactionsResponse,
} from '@erp/rpc';

// Re-export types for convenience
export type {
  SendWorkbenchTransactionsInput,
  SendWorkbenchTransactionsResponse,
};

/**
 * Submission info returned for each division batch
 */
export type SubmissionInfo =
  SendWorkbenchTransactionsResponse['submissions'][number];

/**
 * Failed transaction info
 */
export type FailedTransaction =
  SendWorkbenchTransactionsResponse['failed'][number];

interface StatsData {
  transactionCount: number;
  totalDebitAmount: number;
  vendorCount: number;
}

interface MutationContext {
  previousStats: StatsData | undefined;
}

/**
 * Hook for sending workbench transactions to the 844 external service
 *
 * This hook submits transactions grouped by division and returns
 * detailed results including successful and failed transaction IDs.
 */
export function useSendWorkbenchTransactions() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.shipAndDebit.edi844Submissions.sendWorkbenchTransactions.mutationOptions(
      {
        // Optimistically update the UI
        onMutate: async (variables) => {
          // Cancel any outgoing refetches to avoid overwriting optimistic update
          await queryClient.cancelQueries({
            queryKey: ['rexel/ship-and-debit/844/stats'],
          });
          await queryClient.cancelQueries({
            queryKey: ['rexel/ship-and-debit/844/transactions'],
          });

          // Get current data
          const previousStats = queryClient.getQueryData<StatsData>([
            'rexel/ship-and-debit/844/stats',
          ]);

          // Optimistically update stats
          if (previousStats) {
            queryClient.setQueryData<StatsData>(
              ['rexel/ship-and-debit/844/stats'],
              (old) => {
                if (!old) return old;
                return {
                  ...old,
                  transactionCount: Math.max(
                    0,
                    old.transactionCount - variables.transactionIds.length,
                  ),
                };
              },
            );
          }

          // Return previous values for rollback
          return { previousStats } as MutationContext;
        },

        // If mutation fails, roll back optimistic updates
        onError: (_err, _variables, context) => {
          const ctx = context as MutationContext;
          if (ctx?.previousStats) {
            queryClient.setQueryData(
              ['rexel/ship-and-debit/844/stats'],
              ctx.previousStats,
            );
          }
        },

        // After success or error, refetch to get actual data
        onSettled: () => {
          queryClient.refetchQueries({
            queryKey: ['rexel/ship-and-debit/844/stats'],
          });
          queryClient.refetchQueries({
            queryKey: ['rexel/ship-and-debit/844/data-by-vendor'],
          });
          queryClient.refetchQueries({
            queryKey: ['rexel/ship-and-debit/844/transactions'],
          });
        },
      },
    ),
  );
}
